import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { TimePicker } from "./ui/time-picker";
import { formatTime, timeToMinutes, minutesToTime } from "../lib/utils";
import { Coffee, Utensils, Clock, AlertTriangle } from "lucide-react";
import { useState, useCallback, useMemo } from "react";

// Icon mapping
const BREAK_ICON_MAP = {
  coffee: Coffee,
  utensils: Utensils,
  clock: Clock,
};

const getBreakIcon = (iconName) => BREAK_ICON_MAP[iconName] || Coffee;

// Inner component that handles the break state
const BreakRequiredDialogContent = ({
  employee,
  startTime,
  endTime,
  requiredBreaks = [],
  breakTypes = [],
  snapInterval = 15,
  onConfirm,
  onCancel,
}) => {
  // Snap time to interval
  const snapToInterval = useCallback((time) => {
    if (!time) return "12:00";
    const [hours, minutes] = time.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes;
    const snappedMinutes = Math.round(totalMinutes / snapInterval) * snapInterval;
    const newHours = Math.floor(snappedMinutes / 60) % 24;
    const newMinutes = snappedMinutes % 60;
    return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
  }, [snapInterval]);

  // Compute initial breaks only once when component mounts
  const initialBreaks = useMemo(() => {
    if (!startTime || !endTime || requiredBreaks.length === 0) return [];
    
    const shiftStart = timeToMinutes(startTime);
    const shiftEnd = timeToMinutes(endTime);
    const midpoint = Math.floor((shiftStart + shiftEnd) / 2);

    return requiredBreaks.map((bt) => {
      const breakStartTime = snapToInterval(minutesToTime(midpoint));
      const breakEndTime = minutesToTime(timeToMinutes(breakStartTime) + bt.duration_minutes);

      return {
        break_type_id: bt.id,
        start_time: breakStartTime,
        end_time: snapToInterval(breakEndTime),
      };
    });
  }, [startTime, endTime, requiredBreaks, snapToInterval]);

  const [breaks, setBreaks] = useState(initialBreaks);

  // Update a break's start time (end time auto-calculates from duration)
  const updateBreakStartTime = (index, value) => {
    const snapped = snapToInterval(value);
    const breakTypeId = breaks[index].break_type_id;
    const breakType = requiredBreaks.find((bt) => bt.id === breakTypeId) || 
                      breakTypes.find((bt) => bt.id === breakTypeId);
    const duration = breakType?.duration_minutes || 30;

    // Calculate end time from start + duration
    const breakEndMins = timeToMinutes(snapped) + duration;
    const breakEnd = minutesToTime(breakEndMins);

    setBreaks((prev) =>
      prev.map((b, i) =>
        i === index ? { ...b, start_time: snapped, end_time: snapToInterval(breakEnd) } : b
      )
    );
  };

  const handleConfirm = () => {
    onConfirm({
      start_time: startTime,
      end_time: endTime,
      breaks,
    });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Break Required
        </DialogTitle>
        <DialogDescription>
          This shift requires a break. Please set the break time below.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        {/* Shift Summary */}
        <div className="p-3 rounded-lg bg-muted/50 text-center">
          <p className="text-sm text-muted-foreground">Shift for {employee?.name}</p>
          <p className="text-lg font-semibold tabular-nums">
            {formatTime(startTime)} - {formatTime(endTime)}
          </p>
        </div>

        {/* Required Breaks */}
        {breaks.map((brk, index) => {
          const breakType = requiredBreaks.find((bt) => bt.id === brk.break_type_id) ||
                            breakTypes.find((bt) => bt.id === brk.break_type_id);
          if (!breakType) return null;

          const IconComp = getBreakIcon(breakType.icon);

          return (
            <div key={index} className="p-3 rounded-lg border bg-muted/30 space-y-3">
              <div className="flex items-center gap-2">
                <IconComp className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">{breakType.name}</span>
                <span className="text-xs bg-amber-500/20 text-amber-600 px-1.5 py-0.5 rounded ml-auto">
                  Required
                </span>
              </div>
              
              <div className="text-xs text-muted-foreground">
                {breakType.duration_minutes} minutes • {breakType.is_paid ? "Paid" : "Unpaid"}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Start Time</Label>
                  <TimePicker
                    value={brk.start_time}
                    onChange={(newTime) => updateBreakStartTime(index, newTime)}
                    minTime={startTime}
                    maxTime={endTime}
                    snapInterval={snapInterval}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">End Time</Label>
                  <div className="h-9 px-3 flex items-center rounded-md border bg-muted/50 text-sm tabular-nums text-muted-foreground">
                    {formatTime(brk.end_time)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} data-testid="break-confirm-btn">
          Add Shift
        </Button>
      </DialogFooter>
    </>
  );
};

const BreakRequiredDialog = ({
  open,
  onOpenChange,
  employee,
  startTime,
  endTime,
  requiredBreaks = [],
  breakTypes = [],
  snapInterval = 15,
  onConfirm,
  onCancel,
}) => {
  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  // Create a unique key based on the shift data to reset the inner component when data changes
  const contentKey = `${startTime}-${endTime}-${requiredBreaks.map(b => b.id).join(',')}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="break-required-dialog">
        {open && startTime && endTime && (
          <BreakRequiredDialogContent
            key={contentKey}
            employee={employee}
            startTime={startTime}
            endTime={endTime}
            requiredBreaks={requiredBreaks}
            breakTypes={breakTypes}
            snapInterval={snapInterval}
            onConfirm={onConfirm}
            onCancel={handleCancel}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BreakRequiredDialog;
